import { useMemo, useState } from 'react';
import { ChevronRightIcon, XIcon } from 'lucide-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';
import type { DocState, DocumentNode } from '@worldathletics/types';
import type { SearchParams } from '@/types';
import {
  PageContentLayout,
  PageContentLayoutHeader,
  PageContentLayoutMain,
  PageContentLayoutSubheader,
} from '@/components/layout';
import {
  NodeCard,
  LoadingCardNodePlaceholder,
  Button,
  StatusBadge,
  Dropdown,
  type DropdownOptionProps,
} from '@/components/atoms';
import { CustomTitle } from '@/components/typography';
import { ModalComponent } from '@/components/modal';
import { AddNewLanguage, AddNewVersion, ConfirmModal } from '@/components/modals';
import { MODAL_ADD_LANGUAGE_STYLES, MODAL_CONFIRM_STYLES } from '@/components/modal/constants';
import { PermissionGate } from '@/context/auth/permission-gate';
import { Permission } from '@worldathletics/utilities';
import { usePermission } from '@/hooks/usePermission';
import type { RootState } from '@/context/store/store';
import {
  setAddLangModal,
  setAddVersionModal,
} from '@/context/store/slices/siteSlice';
import { useDocumentQuery } from '@/query/book/useDocumentQuery';
import { useRestoreBookMutation } from '@/query/book/useRestoreBookMutation';
import { useDocumentNodesQuery } from '@/query/node/useDocumentNodesQuery';
import { findDraftNodeForEdit } from '@/lib/find-draft-node-for-edit';
import { formatDateTime } from '@/lib/format-date';
import { getApiErrorMessage } from '@/lib/api-error';
import { exitToPreviewMode } from '@/lib/exit-to-preview-mode';
import { StatusToDisplayLabel } from '@/lib/consts';
import '@/styles/route-documentid.scss';

const languageOptions: DropdownOptionProps[] = [
  { value: '', label: 'All languages' },
  { value: 'EN', label: 'English' },
  { value: 'FR', label: 'French' },
];

const stateOptions: DropdownOptionProps[] = [
  { value: '', label: 'All documents' },
  { value: 'PUBLISHED', label: StatusToDisplayLabel.PUBLISHED },
  { value: 'SCHEDULED', label: StatusToDisplayLabel.SCHEDULED },
  { value: 'REVIEW', label: StatusToDisplayLabel.REVIEW },
  { value: 'DRAFT', label: StatusToDisplayLabel.DRAFT },
  { value: 'ARCHIVED', label: StatusToDisplayLabel.ARCHIVED },
  { value: 'DELETED', label: StatusToDisplayLabel.DELETED },
];

export const Route = createFileRoute('/_auth/document/$documentId')({
  loader: ({ params, location }) => ({
    documentId: params.documentId,
    params: location.search as SearchParams,
  }),
  component: DocumentPage,
});

function DocumentPage() {
  const navigate = useNavigate();
  const { documentId } = Route.useLoaderData();
  const addNewLangModal = useSelector(
    (state: RootState) => state.site.addNewLangModal,
  );
  const addNewVersionModal = useSelector(
    (state: RootState) => state.site.addNewVersionModal,
  );
  const dispatch = useDispatch();

  const documentBook = useDocumentQuery(documentId);
  const documentNodes = useDocumentNodesQuery(documentId);

  const sortedNodes = useMemo(() => {
    const nodes = documentNodes.data ?? [];
    const statusPriority: Record<DocState, number> = {
      PUBLISHED: 0,
      SCHEDULED: 1,
      REVIEW: 2,
      DRAFT: 3,
      ARCHIVED: 4,
      DELETED: 5,
    };
    return [...nodes].sort((a, b) => {
      const diff = statusPriority[a.state] - statusPriority[b.state];
      if (diff !== 0) return diff;
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
  }, [documentNodes.data]);

  const [selectedLanguage, setSelectedLanguage] = useState<string>('');
  const [selectedState, setSelectedState] = useState<string>('');
  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);

  const canRestore = usePermission(Permission.DocumentsRestore);
  const restoreBook = useRestoreBookMutation();

  const filteredNodes = useMemo(() => {
    return sortedNodes.filter(node => {
      const languageMatch =
        !selectedLanguage || node.language === selectedLanguage;
      const stateMatch = !selectedState || node.state === selectedState;
      return languageMatch && stateMatch;
    });
  }, [sortedNodes, selectedLanguage, selectedState]);

  const handleAddLanguage = () => dispatch(setAddLangModal(true));
  const handleAddVersion = () => dispatch(setAddVersionModal(true));

  const handleRestoreBook = async () => {
    setIsRestoring(true);
    try {
      await restoreBook.mutateAsync(documentId);
      toast.success('Document restored');
      setIsRestoreModalOpen(false);
      const { data: nodes } = await documentNodes.refetch();
      const draft = findDraftNodeForEdit(nodes ?? []);
      if (draft) {
        exitToPreviewMode(navigate, dispatch, {
          docId: documentId,
          nodeId: draft.id,
          language: draft.language,
        });
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to restore document'));
    } finally {
      setIsRestoring(false);
    }
  };

  if (documentBook.isLoading || documentNodes.isLoading) {
    return (
      <PageContentLayout>
        <PageContentLayoutMain>
          <LoadingCardNodePlaceholder />
        </PageContentLayoutMain>
      </PageContentLayout>
    );
  }

  if (documentBook.isError || !documentBook.data) {
    return (
      <PageContentLayout>
        <PageContentLayoutMain>
          <p>Document not found.</p>
          <Button
            variant="default"
            onClick={() => navigate({ to: '/documents' })}
          >
            Back to documents
          </Button>
        </PageContentLayoutMain>
      </PageContentLayout>
    );
  }

  const book = documentBook.data;

  return (
    <PageContentLayout>
      <PageContentLayoutHeader
        buttons={
          <Button
            variant="alert"
            type="button"
            onClick={() => navigate({ to: '/documents' })}
          >
            Cancel
            <XIcon size={14} />
          </Button>
        }
      >
        <CustomTitle level="h1">Document Details</CustomTitle>
      </PageContentLayoutHeader>

      <PageContentLayoutSubheader className="document-node-view__states">
        <Dropdown
          label="Language"
          className="document-node-view__filter-dropdown"
          options={languageOptions}
          defaultValue=""
          onChange={opt => setSelectedLanguage(opt.value)}
        />
        <Dropdown
          label="State"
          className="document-node-view__filter-dropdown"
          options={stateOptions}
          defaultValue=""
          onChange={opt => setSelectedState(opt.value)}
        />
        <div className="document-node-view__add-buttons">
          {book.state === 'DELETED' && canRestore ? (
            <Button
              variant="default"
              type="button"
              onClick={() => setIsRestoreModalOpen(true)}
            >
              Restore document <ChevronRightIcon size={16} />
            </Button>
          ) : (
            <>
              <PermissionGate permission={Permission.DocumentsCreate}>
                <Button
                  variant="default"
                  type="button"
                  onClick={handleAddLanguage}
                >
                  Add language <ChevronRightIcon size={16} />
                </Button>
              </PermissionGate>
              <PermissionGate permission={Permission.DocumentsCreate}>
                <Button
                  variant="default"
                  type="button"
                  onClick={handleAddVersion}
                >
                  Add version <ChevronRightIcon size={16} />
                </Button>
              </PermissionGate>
            </>
          )}
        </div>
      </PageContentLayoutSubheader>

      <PageContentLayoutMain>
        <div className="document-node-view__content--cards">
          <div className="document-node-view__content--document">
            <div>
              Name:
              <span className="document-node-view__content--document-span">
                {book.name || '-'}
              </span>
            </div>
            <div>
              Author:
              <span className="document-node-view__content--document-span">
                {book.author || '-'}
              </span>
            </div>
            <div>
              Created at:
              <span className="document-node-view__content--document-span">
                {formatDateTime(book.createdAt)}
              </span>
            </div>
            <div>
              Date published:
              <span className="document-node-view__content--document-span">
                {formatDateTime(book.datePublished)}
              </span>
            </div>
            <StatusBadge state={book.state} style={{ width: 'fit-content' }} />
            {book.state === 'DELETED' && book.purgeEstimatedAt ? (
              <div className="document-node-view__content--document-span">
                Will be permanently deleted on{' '}
                {formatDateTime(book.purgeEstimatedAt)} unless restored
              </div>
            ) : null}
          </div>
          <div className="document-node-view__content--nodes">
            {filteredNodes.map(node => (
              <NodeCard
                key={node.id}
                archiveDisabled={book.state === 'ARCHIVED'}
                bookState={book.state}
                {...(node as unknown as DocumentNode)}
              />
            ))}
          </div>
        </div>
      </PageContentLayoutMain>

      <ModalComponent
        isOpen={addNewLangModal}
        setIsOpen={(b: boolean) => dispatch(setAddLangModal(b))}
        body={<AddNewLanguage bookId={documentId} />}
        title="Add new language"
        style={MODAL_ADD_LANGUAGE_STYLES}
      />
      <ModalComponent
        isOpen={addNewVersionModal}
        setIsOpen={(b: boolean) => dispatch(setAddVersionModal(b))}
        body={<AddNewVersion bookId={documentId} />}
        title="Add new version"
        style={MODAL_ADD_LANGUAGE_STYLES}
      />
      <ModalComponent
        isOpen={isRestoreModalOpen}
        setIsOpen={setIsRestoreModalOpen}
        title="Restore document"
        style={MODAL_CONFIRM_STYLES}
        body={
          <ConfirmModal
            message="Restore this document? It will become visible again and its status will be recalculated from existing versions."
            confirmLabel={isRestoring ? 'Restoring…' : 'Restore'}
            cancelLabel="Cancel"
            onConfirm={() => void handleRestoreBook()}
            onCancel={() => setIsRestoreModalOpen(false)}
            isPending={isRestoring}
          />
        }
      />
    </PageContentLayout>
  );
}
